import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface Step1ResponsibilitiesProps {
  responsibilities: string[];
  selectedResponsibilities: string[];
  onSelectionChange: (selected: string[]) => void;
  onNext: () => void;
  onCancel: () => void;
}

export const Step1Responsibilities: React.FC<Step1ResponsibilitiesProps> = ({
  responsibilities,
  selectedResponsibilities,
  onSelectionChange,
  onNext,
  onCancel
}) => {
  const handleToggleResponsibility = (responsibility: string) => {
    if (selectedResponsibilities.includes(responsibility)) {
      onSelectionChange(selectedResponsibilities.filter(r => r !== responsibility));
    } else {
      onSelectionChange([...selectedResponsibilities, responsibility]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(responsibilities);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            Advanced Tailoring - Step 1 of 3
          </CardTitle>
          <Badge variant="secondary" className="px-3 py-1">
            Responsibilities
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            Select the responsibilities that apply to your experience
          </p>
          <div className="text-sm text-muted-foreground">
            {selectedResponsibilities.length} of {responsibilities.length} selected
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Selection Controls */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSelectAll}
          >
            Select All
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDeselectAll}
          >
            Deselect All
          </Button>
        </div>

        {/* Responsibilities List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {responsibilities.map((responsibility, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => handleToggleResponsibility(responsibility)}
            >
              <Checkbox
                checked={selectedResponsibilities.includes(responsibility)}
                onChange={() => handleToggleResponsibility(responsibility)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-primary">
                    #{index + 1}
                  </span>
                  <Badge 
                    variant={index < 5 ? "default" : index < 10 ? "secondary" : "outline"}
                    className="text-xs"
                  >
                    {index < 5 ? "High Priority" : index < 10 ? "Medium Priority" : "Lower Priority"}
                  </Badge>
                </div>
                <p className="text-sm leading-relaxed">{responsibility}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={onNext}
            disabled={selectedResponsibilities.length === 0}
          >
            Continue to Requirements
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};